import type { VifDB } from '../storage/index.js';

/**
 * Web durability posture (PRD §10): library storage is best-effort. We
 * REQUEST navigator.storage.persist() at first import; the grant is
 * browser-discretionary and never promised to the user — when it is not
 * granted, a dismissible banner states the reality and points at the two
 * real mitigations: installing the PWA and exporting a backup.
 */

const REQUESTED_FLAG = 'storage:persist-requested';
const DISMISSED_FLAG = 'storage:banner-dismissed';

export type DurabilityStatus = 'granted' | 'denied' | 'unsupported';

export async function requestDurableStorage(db: VifDB): Promise<DurabilityStatus> {
  await db.put('kv', new Date().toISOString(), REQUESTED_FLAG);
  const storage = navigator.storage;
  if (storage?.persist === undefined) return 'unsupported';
  try {
    if (await storage.persisted?.()) return 'granted';
    return (await storage.persist()) ? 'granted' : 'denied';
  } catch {
    return 'unsupported';
  }
}

export async function alreadyRequested(db: VifDB): Promise<boolean> {
  return (await db.get('kv', REQUESTED_FLAG)) !== undefined;
}

export async function bannerDismissed(db: VifDB): Promise<boolean> {
  return (await db.get('kv', DISMISSED_FLAG)) !== undefined;
}

export async function dismissBanner(db: VifDB): Promise<void> {
  await db.put('kv', new Date().toISOString(), DISMISSED_FLAG);
}

// ── PWA install nudge ────────────────────────────────────────────────────
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
}

let deferredInstall: BeforeInstallPromptEvent | null = null;

/** Call once at boot; Chrome-family browsers fire this when installable. */
export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstall = event as BeforeInstallPromptEvent;
  });
}

export function canPromptInstall(): boolean {
  return deferredInstall !== null;
}

export async function promptInstall(): Promise<void> {
  const prompt = deferredInstall;
  deferredInstall = null;
  await prompt?.prompt();
}
